// src/components/GestionCompras/GestionCompras.js
import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaSearch, FaFileExcel, FaPrint, FaPlus, FaDollarSign, FaEye } from 'react-icons/fa';
import './GestionCompras.css';
import Header from '../common/Header';
import Footer from '../common/Footer';
import comprasService from '../../services/ComprasService';
import AgregarCompraModal from './AgregarCompraModal';
import AbonarCompraModal from './AbonarCompraModal';
import DetalleCompraModal from './DetalleCompraModal';
const GestionCompras = () => {
  // Estados simplificados
  const [compras, setCompras] = useState([]);
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [showAgregarModal, setShowAgregarModal] = useState(false);
  const [showAbonarModal, setShowAbonarModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [compraSeleccionada, setCompraSeleccionada] = useState(null);

  // Función para cargar compras
  const cargarCompras = async () => {
    setCargando(true);
    setError('');

    try {
      // Cargar todas las compras sin filtros complejos
      const comprasData = await comprasService.getCompras();
      setCompras(comprasData || []);
    } catch (error) {
      console.error('Error al cargar compras:', error);
      setError('Error al cargar las compras: ' + error.message);
      setCompras([]);
    } finally {
      setCargando(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    cargarCompras();
  }, []);

  // Función para manejar la creación de compra
  const handleCompraCreada = () => {
    cargarCompras(); // Recargar la lista
    setShowAgregarModal(false);
  };

  // Función para manejar abono
  const handleAbonar = (compra) => {
    setCompraSeleccionada(compra);
    setShowAbonarModal(true);
  };

  // Función para manejar la creación de abono
  const handleAbonoCreado = () => {
    cargarCompras(); // Recargar la lista
    setShowAbonarModal(false);
    setCompraSeleccionada(null);
  };

  // Función para manejar ver detalle
  const handleVerDetalle = (compra) => {
    setCompraSeleccionada(compra);
    setShowDetalleModal(true);
  };

  // Filtrar compras por búsqueda de texto
  const comprasFiltradas = compras.filter(compra => {
    if (!busquedaTexto) return true;

    const textoBusqueda = busquedaTexto.toLowerCase();
    return (
      compra.compra?.toLowerCase().includes(textoBusqueda) ||
      compra.referencia?.toLowerCase().includes(textoBusqueda) ||
      compra.proveedor?.toLowerCase().includes(textoBusqueda) ||
      compra.estatus?.toLowerCase().includes(textoBusqueda)
    );
  });

  // Calcular totales
  const calcularTotales = () => {
    let totalMonto = 0;
    let totalAbonado = 0;
    let totalSaldo = 0;

    comprasFiltradas.forEach(compra => {
      totalMonto += compra.monto || 0;
      totalAbonado += compra.abonado || 0;
      totalSaldo += compra.saldo || 0;
    });

    return { totalMonto, totalAbonado, totalSaldo };
  };

  const { totalMonto, totalAbonado, totalSaldo } = calcularTotales();

  return (
    <div className="gestion-compras-container">
      <Header
        titulo='Compras'
        color='#667eea' />
      <div className="gestion-compras-content">
        <div className="gestion-compras-inner">
          <div className="compras-header">
            <h1 className="compras-titulo">Gestión de Compras</h1>
            <button
              className="btn-nueva-compra"
              onClick={() => setShowAgregarModal(true)}
            >
              <FaPlus /> Nueva Compra
            </button>
          </div>

          <div className="panel-filtros">
            <div className="filtros-header">
              <h3 className="filtros-titulo">🔍 Control de Compras</h3>
            </div>

            <div className="filtros-content">
              <div className="filtros-row-horizontal">
                <div className="filtro-item">
                  <label>Buscar</label>
                  <div className="busqueda-group">
                    <FaSearch className="busqueda-icon" />
                    <input
                      type="text"
                      value={busquedaTexto}
                      onChange={(e) => setBusquedaTexto(e.target.value)}
                      placeholder="Buscar por compra, referencia, proveedor..."
                      className="input-busqueda"
                    />
                    {busquedaTexto && (
                      <button
                        onClick={() => setBusquedaTexto('')}
                        className="btn-limpiar-busqueda"
                        title="Limpiar búsqueda"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                <div className="filtro-boton-buscar">
                  <button
                    className="btn-buscar"
                    onClick={cargarCompras}
                    disabled={cargando}
                  >
                    <FaSearch />
                    {cargando ? 'Cargando...' : 'Actualizar'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="panel-listado">
            <h3 className="listado-titulo">Listado de Compras ({comprasFiltradas.length})</h3>

            <div className="tabla-container">
              {error && (
                <div className="error-message" style={{ color: 'red', padding: '10px', textAlign: 'center' }}>
                  {error}
                </div>
              )}
              {cargando ? (
                <div className="cargando">Cargando datos...</div>
              ) : comprasFiltradas.length === 0 ? (
                <div className="sin-datos">
                  {busquedaTexto ?
                    `No se encontraron compras que coincidan con "${busquedaTexto}"` :
                    'No hay compras registradas'
                  }
                </div>
              ) : (
                <table className="tabla-compras">
                  <thead>
                    <tr>
                      <th>Compra</th>
                      <th>Fecha</th>
                      <th>Proveedor</th>
                      <th>Monto</th>
                      <th>Abonado</th>
                      <th>Saldo</th>
                      <th>Estatus</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comprasFiltradas.map((compra) => (
                      <tr key={compra.id}>
                        <td>{compra.compra}</td>
                        <td>{compra.fecha}</td>
                        <td>{compra.proveedor}</td>
                        <td className="columna-numerica">${(compra.monto || 0).toFixed(2)}</td>
                        <td className="columna-numerica">${(compra.abonado || 0).toFixed(2)}</td>
                        <td className="columna-numerica">${(compra.saldo || 0).toFixed(2)}</td>
                        <td className={`estatus-${compra.estatus?.toLowerCase() || 'pendiente'}`}>
                          {compra.estatus}
                        </td>
                        <td className="columna-acciones">
                          <button
                            className="btn-ver-detalle"
                            onClick={() => handleVerDetalle(compra)}
                            title="Ver detalle"
                          >
                            <FaEye /> Ver
                          </button>
                          {(compra.estatus === 'PENDIENTE' || compra.estatus === 'PARCIAL') && (compra.saldo || 0) > 0 && (
                            <button
                              className="btn-abonar"
                              onClick={() => handleAbonar(compra)}
                              title="Realizar abono"
                            >
                              <FaDollarSign /> Abonar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="total-label">Totales:</td>
                      <td className="columna-numerica total-valor">${totalMonto.toFixed(2)}</td>
                      <td className="columna-numerica total-valor">${totalAbonado.toFixed(2)}</td>
                      <td className="columna-numerica total-valor">${totalSaldo.toFixed(2)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            <div className="acciones-container">
              <div className="info-compras">
                <span>
                  Total de compras: <strong>{comprasFiltradas.length}</strong>
                  {busquedaTexto && ` de ${compras.length}`}
                </span>
              </div>

              <div className="exportar-opciones">
                <button className="btn-exportar">
                  <FaFileExcel className="icono-exportar" />
                  Exportar
                </button>
                <button className="btn-imprimir">
                  <FaPrint className="icono-imprimir" />
                  Imprimir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      <AgregarCompraModal
        isOpen={showAgregarModal}
        onClose={() => setShowAgregarModal(false)}
        onCompraCreada={handleCompraCreada}
      />

      <AbonarCompraModal
        isOpen={showAbonarModal}
        onClose={() => setShowAbonarModal(false)}
        compra={compraSeleccionada}
        onAbonoCreado={handleAbonoCreado}
      />

      <DetalleCompraModal
        isOpen={showDetalleModal}
        onClose={() => setShowDetalleModal(false)}
        compra={compraSeleccionada}
      />
    </div>
  );
};

export default GestionCompras;